/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { DEAL_PANEL_CLASS, DealPanelLabel } from './DealPanel';
import type {
  DealProposal,
  DealProposalStatus,
} from '@/services/requests/housien-services/DealsService';
import { formatLocalizedDate } from '@/shared/utils/date';
import { formatDealMoney } from '../utils/deal-state';
import {
  ProposalDetails,
  isConditionallyAccepted,
} from './ProposalDetails';

interface DealProposalsPanelProps {
  proposals: DealProposal[];
  /** True once one offer is accepted: the rest become read-only. */
  frozen: boolean;
  /** False on a paused or decided attendance, where nothing may move. */
  canRecord: boolean;
  /**
   * Whether the attendance has a property yet.
   *
   * An offer is always an offer *on* something, and the server refuses
   * one that names no property. Offering the button anyway meant the
   * broker filled the whole form to be told no by a toast, so the
   * refusal is stated up front, next to where it is fixed.
   */
  hasProperty: boolean;
  onRecord: () => void;
  onAnswer: (proposalId: string, status: DealProposalStatus) => void;
  onDelete: (proposalId: string) => void;
  /** Opens a counter pre-filled with the offer being answered. */
  onCounter: (proposal: DealProposal) => void;
  onSettleCondition: (
    proposalId: string, conditionId: string, state: 'met' | 'failed',
  ) => void;
}

/** The answers a broker can still record, per current status. */
const ANSWERS: Record<DealProposalStatus, readonly DealProposalStatus[]> = {
  sent: ['accepted', 'refused'],
  countered: ['accepted', 'refused'],
  refused: [],
  accepted: [],
};

const STATUS_CLASS: Record<DealProposalStatus, string> = {
  sent: 'border-(--border-medium) text-(--text-secondary)',
  countered: 'border-(--accent-cobre)/40 bg-(--accent-cobre)/10 text-(--accent-cobre)',
  refused: 'border-(--border-subtle) text-(--text-tertiary)',
  accepted: 'border-(--accent-salvia)/40 bg-(--accent-salvia)/12 text-(--accent-salvia)',
};

/**
 * Empty state for a deal where nobody has put a number forward yet.
 */
function ProposalsEmpty() {
  const { t } = useTranslation('features/deals');
  return (
    <div
      data-testid="deal-proposals-empty"
      className="flex flex-col items-center gap-(--space-2) py-(--space-5) text-center"
    >
      <div className="h-2 w-24 rounded-full bg-(--surface-tertiary)" />
      <Text component="span" variant="bodySm" c="var(--text-secondary)" className="mt-(--space-2)">
        {t('detail.proposals.empty')}
      </Text>
      <Text component="span" variant="meta" c="var(--text-tertiary)" className="max-w-80 leading-normal">
        {t('detail.proposals.emptyHint')}
      </Text>
    </div>
  );
}

/**
 * One offer: its value, its property and type, and what became of it.
 *
 * The answers offered come from the offer's own status, not from the
 * attendance: a refused or accepted offer is settled and shows none,
 * which is what keeps a broker from walking back a win.
 */
function ProposalRow({ proposal, frozen, onAnswer, onDelete, onCounter, onSettleCondition }: {
  proposal: DealProposal;
  frozen: boolean;
  onAnswer: (proposalId: string, status: DealProposalStatus) => void;
  onDelete: (proposalId: string) => void;
  onCounter: (proposal: DealProposal) => void;
  onSettleCondition: (
    proposalId: string, conditionId: string, state: 'met' | 'failed',
  ) => void;
}) {
  const { t } = useTranslation('features/deals');
  const answers = frozen ? [] : ANSWERS[proposal.status];
  const canCounter = !frozen && proposal.status === 'sent';
  const conditional = isConditionallyAccepted(proposal);

  return (
    <div
      data-testid="deal-proposal-row"
      className="flex flex-wrap items-center gap-(--space-4) border-b border-(--border-subtle) py-(--space-3)"
    >
      <div className="flex min-w-28 flex-col gap-(--space-0-5)">
        <Text component="span" variant="heading" c="var(--accent-petroleo)" className="[font-weight:var(--font-semibold)] tabular-nums">
          {formatDealMoney(proposal.value) ?? '—'}
        </Text>
        <Text component="span" className="text-[length:var(--text-tag)] tabular-nums text-(--text-tertiary-soft)">
          {formatLocalizedDate(proposal.created_at)}
        </Text>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-(--space-0-5)">
        <Text component="span" variant="bodySm" c="var(--text-primary)">
          {t(`types.${proposal.deal_type}`)}
          {proposal.side && ` · ${t(`detail.proposals.sides.${proposal.side}`)}`}
          {/* A counter says what it answers: the chain is the
              negotiation, and a round shown alone loses that. */}
          {proposal.supersedes && ` · ${t('detail.proposals.isCounter')}`}
        </Text>
        {proposal.actor && (
          <Text component="span" variant="meta" c="var(--text-secondary)">
            {t('detail.proposals.recordedBy', { actor: proposal.actor })}
          </Text>
        )}
        <ProposalDetails
          proposal={proposal}
          canSettle={!frozen || conditional}
          onSettleCondition={onSettleCondition}
        />
      </div>

      <Text
        component="span"
        data-testid="proposal-status-chip"
        className={cn(
          'rounded-full border px-(--space-2) py-(--space-1)',
          'text-[length:var(--text-tag)] uppercase',
          'tracking-(--tracking-chip)',
          STATUS_CLASS[proposal.status],
        )}
      >
        {conditional
          ? t('detail.proposals.statuses.conditional')
          : t(`detail.proposals.statuses.${proposal.status}`)}
      </Text>

      {/*
        No second chip for a passed `valid_until`. Nothing expires on
        its own here -- by design: no job hunts for stale offers, the
        status does not change, and Aceitar stays available. A chip
        reading "vencida" beside the status chip claimed otherwise, and
        the two disagreed with the buttons underneath them. The date
        itself is still shown, as "Venceu em <data>" among the terms,
        which states the fact without inventing a status.
      */}

      {/*
        The three answers share the width equally and stand 40px tall on a
        phone. They were 28px and sized to their own labels, so "Aceitar"
        was a smaller target than "Recusar" purely because the word is
        shorter — the answer you least want to mis-tap being the easiest one
        to hit by accident. Equal width also stops the row reading as a
        ranking it does not mean.
      */}
      <div className="flex items-center gap-(--space-1) max-md:flex-wrap">
        {/* `min-w-0` on the children as well as `flex-1`: without it each
            button's own text sets a floor on its width, and "Contraproposta"
            is twice the word "Aceitar" is. */}
        <div className="flex min-w-0 flex-1 items-center gap-(--space-1)">
          {/* Countering opens a form rather than flipping a status: the
              answer to an offer is another offer, with its own terms. */}
          {canCounter && (
            <Button
              variant="ghost-text"
              size="micro"
              onClick={() => onCounter(proposal)}
              data-testid="proposal-counter-button"
              className="min-w-0 flex-1 max-md:h-10"
            >
              {t('detail.proposals.answers.countered')}
            </Button>
          )}
          {answers.map((status) => (
            <Button
              key={status}
              variant="ghost-text"
              size="micro"
              onClick={() => onAnswer(proposal.proposal_id, status)}
              data-testid={`proposal-answer-${status}`}
              className="min-w-0 flex-1 max-md:h-10"
            >
              {t(`detail.proposals.answers.${status}`)}
            </Button>
          ))}
        </div>
        {/* Deleting is not an answer to the offer, so it keeps its own size
            and does not share the row's equal thirds. */}
        {!frozen && (
          <Button
            variant="ghost-text"
            size="micro"
            onClick={() => onDelete(proposal.proposal_id)}
            data-testid="proposal-delete-button"
            className="shrink-0"
          >
            {t('detail.proposals.delete')}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Propostas panel: every offer recorded against the attendance.
 *
 * Built from the attendance's own `proposals` rather than from its
 * `proposal_recorded` events. The events were all there used to be —
 * the attendance kept only the latest `proposed_value` — so an offer
 * could not be answered, corrected or removed.
 *
 * Recording a new offer lives here too, for the same reason accepting
 * does: an offer is a thing with a list, a status and a history, and the
 * place to make one is the place that shows them.
 *
 * @param props - The offers, whether they are frozen, and the callbacks.
 * @returns The proposals section card.
 */
export function DealProposalsPanel({
  proposals, frozen, canRecord, hasProperty, onRecord, onAnswer, onDelete,
  onCounter, onSettleCondition,
}: DealProposalsPanelProps) {
  const { t } = useTranslation('features/deals');

  return (
    <div data-testid="deal-proposals-panel" className={cn(DEAL_PANEL_CLASS, 'gap-(--space-3)')}>
      <div className="flex items-baseline justify-between gap-(--space-3)">
        <DealPanelLabel>{t('detail.proposals.title')}</DealPanelLabel>
        <div className="flex items-center gap-(--space-3)">
          {proposals.length > 0 && (
            <Text component="span" variant="meta" c="var(--text-tertiary-soft)">
              {t('detail.proposals.count', { count: proposals.length })}
            </Text>
          )}
          {/* Making an offer belongs where the offers are shown. In the
              action bar it had to guess at which stages the action still
              meant something; here the list itself is the context. */}
          {canRecord && !frozen && (
            <Button
              variant="outlined"
              size="sm"
              onClick={onRecord}
              disabled={!hasProperty}
              data-testid="record-proposal-button"
            >
              {t('detail.actions.recordProposal')}
            </Button>
          )}
        </div>
      </div>

      {proposals.length === 0 ? <ProposalsEmpty /> : (
        <div className="flex flex-col">
          {proposals.map((proposal) => (
            <ProposalRow
              key={proposal.proposal_id}
              proposal={proposal}
              frozen={frozen}
              onAnswer={onAnswer}
              onDelete={onDelete}
              onCounter={onCounter}
              onSettleCondition={onSettleCondition}
            />
          ))}
        </div>
      )}

      {canRecord && !frozen && !hasProperty && (
        <Text
          component="span"
          data-testid="proposals-need-property-note"
          className="text-[length:var(--text-tag)] leading-normal text-(--accent-cobre)"
        >
          {t('detail.proposals.needPropertyNote')}
        </Text>
      )}

      {frozen ? (
        <Text component="span" data-testid="proposals-frozen-note" className="text-[length:var(--text-tag)] leading-normal text-(--text-tertiary-soft)">
          {t('detail.proposals.frozenNote')}
        </Text>
      ) : (
        <Text component="span" className="text-[length:var(--text-tag)] leading-normal text-(--text-tertiary-soft)">
          {t('detail.proposals.footnote')}
        </Text>
      )}
    </div>
  );
}
